import { useState, useEffect } from 'react'
import {
  Shield,
  Lock,
  Eye,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  Video,
  Download,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface AuditEntry {
  id: number
  action: string
  user: string
  ip?: string
  timestamp: string
  status: string
}

interface StorageStat {
  type: string
  count: number
  size: string
  retention: string
}

const defaultStorageStats: StorageStat[] = [
  { type: 'Recordings', count: 0, size: '0 GB', retention: '30 days' },
  { type: 'Transcripts', count: 0, size: '0 MB', retention: '90 days' },
  { type: 'Reports', count: 0, size: '0 MB', retention: '1 year' },
  { type: 'Documents', count: 0, size: '0 MB', retention: '1 year' },
]

export default function AdminSecurity() {
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([])
  const [storageStats] = useState<StorageStat[]>(defaultStorageStats)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  useEffect(() => {
    fetchAuditLog()
  }, [page])

  const fetchAuditLog = async () => {
    try {
      const res = await adminApi.getAuditLog({ page, per_page: 20 })
      const data = res.data || res || []
      setAuditLogs(data)
      setHasMore(data.length === 20)
    } catch (err) {
      console.error('Failed to fetch audit log:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="loading-screen">Loading security data...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Security & Compliance</h1>
        <p className="page-subtitle">
          Recording access controls, storage, and audit logs
        </p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon success">
            <Shield size={24} />
          </div>
          <div className="value">Active</div>
          <div className="label">Security Status</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <Lock size={24} />
          </div>
          <div className="value">256-bit</div>
          <div className="label">Encryption</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <Clock size={24} />
          </div>
          <div className="value">30 days</div>
          <div className="label">Recording Retention</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <Eye size={24} />
          </div>
          <div className="value">{auditLogs.length}</div>
          <div className="label">Audit Events</div>
        </div>
      </div>

      {/* Storage & Access Controls */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: 24,
          marginBottom: 24,
        }}
      >
        {/* Storage & Retention */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Storage & Retention
            </h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: 13 }}
            >
              Configure
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {storageStats.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: 16,
                  background: 'var(--accent-light)',
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--primary-navy)',
                  }}
                >
                  {item.type === 'Recordings' ? (
                    <Video size={20} />
                  ) : (
                    <FileText size={20} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {item.type}
                  </h4>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                    }}
                  >
                    {item.count} files &middot; {item.size}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    padding: '6px 12px',
                    background: 'white',
                    borderRadius: 6,
                  }}
                >
                  <Clock size={14} />
                  {item.retention}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Access Controls */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Access Controls</h3>
            <button
              className="btn btn-primary"
              style={{ padding: '8px 16px', fontSize: 13 }}
            >
              Manage Access
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(72, 187, 120, 0.1)',
                  color: 'var(--success)',
                }}
              >
                <CheckCircle size={18} />
              </div>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  Admins
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Full access to all recordings and transcripts
                </p>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(237, 137, 54, 0.1)',
                  color: 'var(--warning)',
                }}
              >
                <AlertTriangle size={18} />
              </div>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  Interviewers
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Access only to their own session recordings
                </p>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(245, 101, 101, 0.1)',
                  color: 'var(--danger)',
                }}
              >
                <XCircle size={18} />
              </div>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  Clients
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  No access to interviewer recordings
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Audit Log</h3>
          <button
            className="btn btn-secondary"
            style={{ padding: '6px 14px', fontSize: 13 }}
          >
            <Download size={14} />
            Export Log
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>User</th>
                <th>IP Address</th>
                <th>Timestamp</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'center',
                      padding: 40,
                      color: 'var(--text-muted)',
                    }}
                  >
                    No audit events recorded
                  </td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={{ fontWeight: 500 }}>{log.action}</td>
                    <td>{log.user}</td>
                    <td
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                      }}
                    >
                      {log.ip || '--'}
                    </td>
                    <td
                      style={{
                        fontSize: 13,
                        color: 'var(--text-muted)',
                      }}
                    >
                      {log.timestamp}
                    </td>
                    <td>
                      <span
                        className={`badge ${log.status === 'success' ? 'badge-success' : 'badge-danger'}`}
                      >
                        {log.status === 'success' ? (
                          <>
                            <CheckCircle size={12} /> Success
                          </>
                        ) : (
                          <>
                            <XCircle size={12} /> Failed
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              marginTop: 16,
            }}
          >
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 14,
                color: 'var(--text-muted)',
              }}
            >
              Page {page}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
              disabled={!hasMore}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
